import React from 'react';
import { View } from 'react-native';
import { Image } from 'expo-image';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { CtaRow, GhostCta, PrimaryCta, Section, useLanding } from './LandingKit';
import { fluid, isWeb, MONO } from './landingTokens';

const EDITORS = [
  {
    kind: 'document', icon: 'file-lines', label: 'Document editor', file: 'Launch brief.docx',
    title: 'Make every word yours.',
    copy: 'Let AI handle the first draft, then refine what matters. Edit directly or ask for rewrites, formatting, tables, and images.',
    detail: 'Rich text · AI rewrites · Word & PDF',
  },
  {
    kind: 'slides', icon: 'file-powerpoint', label: 'PowerPoint editor', file: 'Launch strategy.pptx',
    title: 'From idea to presentation.',
    copy: 'Get your ideas into slides faster. Let AI build the deck, then fine-tune the content, layouts, and theme.',
    detail: 'Slide layouts · Themes · PowerPoint export',
  },
  {
    kind: 'sheet', icon: 'table', label: 'Spreadsheet editor', file: 'Launch budget.xlsx',
    title: 'Put your numbers to work.',
    copy: 'Hand off the spreadsheet setup. Get budgets, comparisons, formulas, and charts, then review and adjust the numbers.',
    detail: 'Formulas · Charts · Excel export',
  },
];

const BUILDERS = [
  {
    kind: 'game', icon: 'gamepad', label: 'Video game builder', file: 'Star runner.html',
    title: 'Dream it. Build it. Play it.',
    copy: 'Describe an arcade game, puzzle, or platformer and watch Gab AI Work Agent bring it to life. Play it beside the chat, then ask for new levels, different controls, or one more challenge.',
    detail: 'Playable previews · AI revisions · Download your game',
  },
  {
    kind: 'code', icon: 'code', label: 'Coding workspace', file: 'Next chapter / index.html',
    title: 'Your idea, up and running.',
    copy: 'Build a website, prototype a useful tool, or automate a task. Gab AI Work Agent writes and runs code on its own computer. Preview the result, inspect the source, and ask AI to keep improving it.',
    detail: 'Websites & tools · Code previews · Keep the source',
  },
];

function SmallText({ children, style }) {
  const { c } = useLanding();
  return <Text style={{ fontSize: 11, lineHeight: 17, color: c.inkSoft, ...style }}>{children}</Text>;
}

// Lightweight illustrations keep the real editors and their dependencies out
// of the public landing bundle. These are examples, not interactive controls.
function EditorPreview({ kind, file }) {
  const { c } = useLanding();
  return (
    <View aria-hidden={isWeb ? true : undefined} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" style={{ height: 238, backgroundColor: c.panel, borderBottomWidth: 1, borderColor: c.rule, overflow: 'hidden' }}>
      <View style={{ height: 39, paddingHorizontal: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: c.card, borderBottomWidth: 1, borderColor: c.rule }}>
        <SmallText>{file}</SmallText><SmallText style={{ color: c.accent }}>Saved</SmallText>
      </View>
      {kind === 'document' ? (
        <View style={{ marginHorizontal: 25, marginTop: 16, padding: 22, backgroundColor: c.cardSolid, borderWidth: 1, borderColor: c.rule, gap: 9 }}>
          <SmallText style={{ color: c.accent, fontSize: 9, letterSpacing: 1.4 }}>THE NEXT CHAPTER</SmallText>
          <Text weight="semibold" style={{ fontSize: 22, color: c.ink }}>A launch worth talking about.</Text>
          <SmallText>Our story starts with a simple idea: make room for something extraordinary.</SmallText>
          <View style={{ backgroundColor: c.accentWash, padding: 7, borderLeftWidth: 2, borderColor: c.accent }}><SmallText>Bring the vision to life, together.</SmallText></View>
        </View>
      ) : kind === 'slides' ? (
        <View style={{ flex: 1, flexDirection: 'row', padding: 15, gap: 12 }}>
          <View style={{ width: 34, gap: 9 }}>
            {['01', '02', '03'].map((n, i) => <View key={n} style={{ height: 29, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: i === 0 ? c.accent : c.rule, backgroundColor: c.card }}><SmallText>{n}</SmallText></View>)}
          </View>
          <View style={{ flex: 1, backgroundColor: c.accentFill, borderRadius: 3, padding: 18, justifyContent: 'space-between' }}>
            <SmallText style={{ color: '#D7EBDD', letterSpacing: 1.3, fontSize: 9 }}>A BIG IDEA. A CLEAR PLAN.</SmallText>
            <Text weight="semibold" style={{ color: '#FFFFFF', fontSize: 26, lineHeight: 29 }}>Meet your{ '\n' }next chapter.</Text>
            <SmallText style={{ color: '#D7EBDD' }}>Launch strategy / 01</SmallText>
          </View>
        </View>
      ) : (
        <View style={{ padding: 15, gap: 10 }}>
          <View style={{ borderWidth: 1, borderColor: c.rule, backgroundColor: c.card, paddingHorizontal: 10, paddingVertical: 5 }}><SmallText style={{ fontFamily: MONO }}>fx   =SUM(B2:B4)</SmallText></View>
          {[
            ['Category', 'Budget', 'Actual'], ['Creative', '$4,000', '$3,600'],
            ['Production', '$8,000', '$7,500'], ['Distribution', '$3,000', '$2,800'], ['Total', '$15,000', '$13,900'],
          ].map((row, i) => <View key={row[0]} style={{ flexDirection: 'row', backgroundColor: i === 0 || i === 4 ? c.accentWash : c.card, marginTop: -10 }}>
            {row.map((cell, j) => <View key={j} style={{ flex: j === 0 ? 1.4 : 1, padding: 7, borderRightWidth: 1, borderBottomWidth: 1, borderColor: c.rule }}><SmallText style={{ fontSize: 10, color: i === 4 ? c.accent : c.inkSoft }}>{cell}</SmallText></View>)}
          </View>)}
        </View>
      )}
    </View>
  );
}

function BuilderPreview({ kind, file }) {
  const { c, isXS } = useLanding();
  return (
    <View aria-hidden={isWeb ? true : undefined} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" style={{ height: 270, backgroundColor: '#111B18', borderBottomWidth: 1, borderColor: c.rule, overflow: 'hidden' }}>
      <View style={{ height: 39, paddingHorizontal: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: c.card, borderBottomWidth: 1, borderColor: c.rule }}>
        <SmallText>{file}</SmallText><SmallText style={{ color: c.accent }}>Preview</SmallText>
      </View>
      {kind === 'game' ? (
        <View style={{ flex: 1, padding: 20, justifyContent: 'space-between', overflow: 'hidden', backgroundColor: '#131D30' }}>
          {Array.from({ length: 18 }, (_, i) => <View key={i} style={{ position: 'absolute', left: `${(i * 37 + 9) % 96}%`, top: `${(i * 23 + 12) % 92}%`, width: i % 3 === 0 ? 3 : 2, height: i % 3 === 0 ? 3 : 2, backgroundColor: '#A7BBD7', opacity: 0.5 }} />)}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <SmallText style={{ color: '#C8DAF1', fontFamily: MONO }}>STAR RUNNER</SmallText>
            <SmallText style={{ color: '#91E2B8', fontFamily: MONO }}>SCORE 0240</SmallText>
          </View>
          <View style={{ alignItems: 'center', gap: 13 }}>
            <View style={{ flexDirection: 'row', gap: isXS ? 23 : 36 }}>
              {[0, 1, 2, 3, 4].map(i => <View key={i} style={{ width: 17, height: 13, borderTopWidth: 4, borderBottomWidth: 4, borderColor: '#C7A6EF', backgroundColor: '#776194', transform: [{ translateY: i % 2 === 0 ? 0 : 10 }] }} />)}
            </View>
            <View style={{ width: 3, height: 18, backgroundColor: '#91E2B8', marginTop: 10 }} />
            <View style={{ alignItems: 'center' }}>
              <View style={{ width: 8, height: 9, backgroundColor: '#E2F5ED' }} />
              <View style={{ width: 26, height: 9, backgroundColor: '#91E2B8' }} />
              <View style={{ width: 38, height: 7, backgroundColor: '#67B68F' }} />
              <View style={{ width: 8, height: 8, backgroundColor: '#F5C584', marginTop: 3 }} />
            </View>
          </View>
          <SmallText style={{ color: '#B8CAE0', textAlign: 'center', fontFamily: MONO, fontSize: 10 }}>← → MOVE   /   SPACE TO SHOOT</SmallText>
        </View>
      ) : (
        <View style={{ flex: 1, flexDirection: 'row' }}>
          <View style={{ flex: 1.15, minWidth: 0, padding: isXS ? 13 : 20, gap: 7 }}>
            <SmallText style={{ color: '#91C8AA', fontFamily: MONO, fontSize: 10 }}>index.html</SmallText>
            {[
              ['<main>', '#91C8AA'], ['  <h1>Next chapter</h1>', '#DCE8E0'],
              ['  <p>Make room for', '#DCE8E0'], ['    your next idea.</p>', '#DCE8E0'],
              ['  <button>Explore</button>', '#DBBAEF'], ['</main>', '#91C8AA'],
            ].map(([line, color], i) => <SmallText key={i} style={{ color, fontFamily: MONO, fontSize: isXS ? 8 : 10, lineHeight: 16, ...(isWeb ? { whiteSpace: 'pre' } : {}) }}>{line}</SmallText>)}
            <SmallText style={{ color: '#91C8AA', fontSize: 10, marginTop: 7 }}>Your idea, taking shape.</SmallText>
          </View>
          <View style={{ flex: 0.85, minWidth: 0, backgroundColor: '#E9EEE4', margin: 14, marginLeft: 0, borderRadius: 4, padding: isXS ? 12 : 18, justifyContent: 'center', gap: 12 }}>
            <SmallText style={{ color: '#385441', fontSize: 8, letterSpacing: 1.5 }}>NEXT CHAPTER</SmallText>
            <Text weight="semibold" style={{ color: '#17392B', fontSize: isXS ? 22 : 28, lineHeight: isXS ? 25 : 31 }}>Make room for your next idea.</Text>
            <View style={{ alignSelf: 'flex-start', paddingVertical: 7, paddingHorizontal: 12, backgroundColor: '#17392B', borderRadius: 3 }}><SmallText style={{ color: '#FFFFFF', fontSize: 10 }}>Explore →</SmallText></View>
          </View>
        </View>
      )}
    </View>
  );
}

function WorkAreaCard({ area, builder = false }) {
  const { c } = useLanding();
  return (
    <View style={{ flex: 1, minWidth: 0, borderWidth: 1, borderColor: c.ruleStrong, borderRadius: 10, overflow: 'hidden', backgroundColor: c.card }}>
      {builder ? <BuilderPreview kind={area.kind} file={area.file} /> : <EditorPreview kind={area.kind} file={area.file} />}
      <View style={{ padding: 24, gap: 14, flex: 1 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}><Icon name={area.icon} size={15} color={c.accent} /><Text weight="medium" style={{ color: c.accent, fontSize: 13 }}>{area.label}</Text></View>
        <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 24, lineHeight: 29, letterSpacing: -0.6 }}>{area.title}</Text>
        <Text style={{ color: c.inkSoft, fontSize: 15, lineHeight: 24, flex: 1 }}>{area.copy}</Text>
        <Text style={{ color: c.accent, fontSize: 12, lineHeight: 20, marginTop: 4 }}>{area.detail}</Text>
      </View>
    </View>
  );
}

function VideoStudioPreview() {
  const { isXS } = useLanding();
  return (
    <View aria-hidden={isWeb ? true : undefined} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" style={{ flex: 1.3, minWidth: 0, backgroundColor: '#111B18', padding: isXS ? 14 : 22, gap: 16 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <SmallText style={{ color: '#E8F0EB' }}>Next chapter / Video studio</SmallText>
        <SmallText style={{ color: '#91C8AA' }}>Project preview</SmallText>
      </View>
      <View style={{ height: isXS ? 180 : 240, borderRadius: 5, overflow: 'hidden', justifyContent: 'flex-end', padding: 22 }}>
        <Image source={require('../../assets/images/landing-mountain.png')} contentFit="cover" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} />
        <View style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.30)' }} />
        <SmallText style={{ color: '#FFFFFF', letterSpacing: 2 }}>MAKE SOMETHING THAT MOVES PEOPLE.</SmallText>
        <Text weight="semibold" style={{ fontSize: isXS ? 29 : 38, lineHeight: isXS ? 34 : 43, color: '#FFFFFF', marginTop: 5 }}>This is your next chapter.</Text>
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><SmallText style={{ color: '#BBCAC1' }}>Shot timeline</SmallText><SmallText style={{ color: '#BBCAC1', fontFamily: MONO }}>00:12 / 00:30</SmallText></View>
      <View style={{ gap: 7 }}>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          {['01 / Opening', '02 / The story', '03 / Reveal'].map((shot, i) => <View key={shot} style={{ flex: 1, height: 45, justifyContent: 'center', paddingHorizontal: 8, borderRadius: 4, backgroundColor: i === 1 ? '#365B48' : '#263B32', borderWidth: 1, borderColor: i === 1 ? '#91C8AA' : '#41564A' }}><SmallText style={{ color: '#E8F0EB', fontSize: 10 }}>{shot}</SmallText></View>)}
        </View>
        <View style={{ backgroundColor: '#354151', borderRadius: 3, paddingHorizontal: 10, paddingVertical: 4 }}><SmallText style={{ color: '#DBE5F2', fontSize: 10 }}>Voiceover / Tell your story</SmallText></View>
        <View style={{ backgroundColor: '#453D50', borderRadius: 3, paddingHorizontal: 10, paddingVertical: 4 }}><SmallText style={{ color: '#E7DEF1', fontSize: 10 }}>Music / A little momentum</SmallText></View>
      </View>
    </View>
  );
}

function LandingWorkAreas() {
  const { c, width, isCompact, isXS } = useLanding();
  const titleSize = fluid(width, 34, 52);
  return (
    <Section label="Gab AI Work Agent areas" style={{ borderTopWidth: 1, borderTopColor: c.rule }} contentStyle={{ paddingTop: fluid(width, 48, 80), paddingBottom: fluid(width, 48, 80) }}>
      <Text style={{ color: c.accent, fontSize: 15, marginBottom: 18 }}>Inside Gab AI Work Agent</Text>
      <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: titleSize, lineHeight: Math.round(titleSize * 1.1), letterSpacing: -titleSize * 0.035, maxWidth: 800, ...(isWeb ? { whiteSpace: 'pre-line' } : {}) }}>Get to the finished version.{ '\n' }Faster.</Text>
      <Text style={{ color: c.inkSoft, fontSize: 18, lineHeight: 29, maxWidth: 690, marginTop: 22 }}>Let Gab AI Work Agent create the video, document, presentation, spreadsheet, game, or website. Review it beside the conversation and ask for changes. Less time switching tools. More time for everything else.</Text>

      <View style={{ marginTop: 38, borderWidth: 1, borderColor: c.ruleStrong, borderRadius: 12, overflow: 'hidden', flexDirection: isCompact ? 'column' : 'row', backgroundColor: c.card }}>
        <View style={{ flex: 1, padding: fluid(width, 24, 40), justifyContent: 'center', gap: 20 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}><Icon name="film" size={18} color={c.accent} /><Text weight="medium" style={{ color: c.accent, fontSize: 14 }}>AI video editing studio</Text></View>
          <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: fluid(width, 28, 36), lineHeight: fluid(width, 34, 42), letterSpacing: -1, ...(isWeb ? { whiteSpace: 'pre-line' } : {}) }}>From the first scene{ '\n' }to the final cut.</Text>
          <Text style={{ color: c.inkSoft, fontSize: 16, lineHeight: 26 }}>Hand off the production and keep the creative direction. AI creates your film; you preview the shots and ask for changes to the visuals, music, or voiceover in one place.</Text>
          <View style={{ gap: 10 }}>
            {['Shape the story on a visual timeline', 'Refine individual shots with AI', 'Review and export your finished video'].map(line => <View key={line} style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}><Icon name="check" size={11} color={c.accent} /><Text style={{ color: c.inkSoft, fontSize: 13, lineHeight: 20, flex: 1 }}>{line}</Text></View>)}
          </View>
        </View>
        <VideoStudioPreview />
      </View>

      <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: 18, marginTop: 18 }}>
        {EDITORS.map(area => <WorkAreaCard key={area.kind} area={area} />)}
      </View>
      <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: 18, marginTop: 18 }}>
        {BUILDERS.map(area => <WorkAreaCard key={area.kind} area={area} builder />)}
      </View>
      <View style={{ marginTop: 28, gap: 18 }}>
        <Text style={{ color: c.inkSoft, fontSize: 15, lineHeight: 24 }}>From the first draft to the final build, spend less time managing the process and more time enjoying what you create.</Text>
        <CtaRow><PrimaryCta restrained label="Create with Gab AI Work Agent" full={isXS} /><GhostCta label="Explore plans" href="/pricing" /></CtaRow>
      </View>
    </Section>
  );
}

export default React.memo(LandingWorkAreas);
